import { useState, useCallback, useEffect } from 'react';
import { type DERCRequest, type DERCFormErrors, type DERCFormData } from '../types/derc';

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface UseDERCFormProps {
  initialDERCRequest: DERCRequest | null;
  isOpen: boolean;
  onSubmit: (dercRequest: DERCRequest) => void;
  defaultIdOrigenProducto?: number;
}

export const useDERCForm = ({
  initialDERCRequest,
  isOpen,
  onSubmit,
  defaultIdOrigenProducto,
}: UseDERCFormProps) => {
  const getDefaultFormData = useCallback((): DERCFormData => ({
    fechaExtraccion: getLocalDateString(),
    numeroDocumento: '',
    idOrigenProducto: defaultIdOrigenProducto?.toString() || '',
    numeroMallas: defaultIdOrigenProducto === 2 ? '0' : '',
    numeroManojos: defaultIdOrigenProducto === 2 ? '0' : '',
    placaVehiculo: defaultIdOrigenProducto === 2 ? 'N/A' : '',
    idAsociacion: defaultIdOrigenProducto === 2 ? '1' : '',
    observacion: '',
    idEspecie: '',
    isEntero: defaultIdOrigenProducto !== 2, // Type A defaults to true (entero), Type B defaults to false
  }), [defaultIdOrigenProducto]);

  const [formData, setFormData] = useState<DERCFormData>(() => getDefaultFormData());

  const [errors, setErrors] = useState<DERCFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Reset form when dialog opens/closes or when initial data changes
  useEffect(() => {
    if (isOpen) {
      const initialData: DERCFormData = initialDERCRequest ? {
        fechaExtraccion: initialDERCRequest.fechaExtraccion ? initialDERCRequest.fechaExtraccion.split('T')[0] : getLocalDateString(),
        numeroDocumento: initialDERCRequest.numeroDocumento || '',
        idOrigenProducto: initialDERCRequest.idOrigenProducto?.toString() || '',
        numeroMallas: initialDERCRequest.numeroMallas?.toString() || '',
        numeroManojos: initialDERCRequest.numeroManojos?.toString() || '',
        placaVehiculo: initialDERCRequest.placaVehiculo || '',
        idAsociacion: initialDERCRequest.idAsociacion?.toString() || '',
        observacion: initialDERCRequest.observacion || '',
        idEspecie: initialDERCRequest.idEspecie?.toString() || '',
        isEntero: initialDERCRequest.isEntero || false,
      } : getDefaultFormData();

      setFormData(initialData);
      setErrors({});
      setIsDirty(false);
    }
  }, [isOpen, initialDERCRequest, defaultIdOrigenProducto]);

  const validateField = useCallback((name: keyof DERCFormData, value: any): string | undefined => {
    // For mode 2 (Tipo B), only validate fechaExtraccion and observacion
    const isMode2 = defaultIdOrigenProducto === 2;

    switch (name) {
      case 'fechaExtraccion':
        if (!value) return 'La fecha de extracción es requerida';
        break;
      case 'numeroDocumento':
        if (!value?.trim()) return 'El número de documento es requerido';

        // For both Type A and Type B, validate the format: xxxx-yyyy-mm/SEC
        // Type B will be correlative but uses the same format
        const documentPattern = /^(\d{1,4}(-\d{4}-\d{2}\/SEC)?)?$/;
        if (value.trim() && !documentPattern.test(value.trim())) {
          return isMode2
            ? 'El formato del documento debe ser xxxx-yyyy-mm/SEC (correlativo)'
            : 'El formato del documento debe ser xxxx-yyyy-mm/SEC';
        }

        // If it's just a number, ensure it's at least 1 digit
        const numberOnly = value.replace(/[^\d]/g, '');
        if (numberOnly && numberOnly.length < 1) {
          return 'El número de documento debe tener al menos 1 dígito';
        }
        break;
      case 'idOrigenProducto':
        if (isMode2) break; // Not required for mode 2, it's set by default
        if (!value || value === '') return 'El origen del producto es requerido';
        const origenNum = Number(value);
        if (isNaN(origenNum) || origenNum <= 0) return 'Debe seleccionar un origen de producto válido';
        break;
      case 'numeroMallas':
        if (!value || value === '') return 'El número de mallas es requerido';
        const mallasNum = Number(value);
        if (isNaN(mallasNum) || mallasNum < 0) return 'El número de mallas debe ser un número válido mayor o igual a 0';
        break;
      case 'numeroManojos':
        if (isMode2) break; // Not required for mode 2
        if (!value || value === '') return 'El número de manojos es requerido';
        const manojosNum = Number(value);
        if (isNaN(manojosNum) || manojosNum < 0) return 'El número de manojos debe ser un número válido mayor o igual a 0';
        break;
      case 'placaVehiculo':
        if (isMode2) break; // Not required for mode 2
        if (!value?.trim()) return 'La placa del vehículo es requerida';
        if (value.trim().length < 6) return 'La placa del vehículo debe tener al menos 6 caracteres';
        break;
      case 'idAsociacion':
        if (isMode2) break; // Not required for mode 2, it's set by default
        if (!value || value === '') return 'La asociación es requerida';
        const asociacionNum = Number(value);
        if (isNaN(asociacionNum) || asociacionNum <= 0) return 'Debe seleccionar una asociación válida';
        break;
      case 'idEspecie':
        if (!value || value === '') return 'La especie es requerida';
        const especieNum = Number(value);
        if (isNaN(especieNum) || especieNum <= 0) return 'Debe seleccionar una especie válida';
        break;
      case 'observacion':
        // Observacion is optional, no validation needed
        break;
      default:
        return undefined;
    }
    return undefined;
  }, [defaultIdOrigenProducto]);

  const validateForm = useCallback((): boolean => {
    const newErrors: DERCFormErrors = {};

    // Validate all fields (only those that can have errors)
    const validatableFields: (keyof DERCFormErrors)[] = [
      'fechaExtraccion',
      'numeroDocumento',
      'idOrigenProducto',
      'numeroMallas',
      'numeroManojos',
      'placaVehiculo',
      'idAsociacion',
      'observacion',
      'idEspecie',
    ];

    validatableFields.forEach((fieldName) => {
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateField]);

  const handleFieldChange = useCallback(
    (name: keyof DERCFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setIsDirty(true);

      // Clear error for this field when user starts typing
      if (errors[name as keyof DERCFormErrors]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name as keyof DERCFormErrors];
          return newErrors;
        });
      }

      // Validate field in real-time
      const error = validateField(name, value);
      if (error) {
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
    },
    [errors, validateField]
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const dercRequest: DERCRequest = {
        ...(initialDERCRequest?.id && { id: initialDERCRequest.id }),
        fechaExtraccion: formData.fechaExtraccion,
        numeroDocumento: formData.numeroDocumento.trim(),
        idOrigenProducto: Number(formData.idOrigenProducto),
        numeroMallas: Number(formData.numeroMallas),
        numeroManojos: Number(formData.numeroManojos),
        placaVehiculo: formData.placaVehiculo.trim().toUpperCase(),
        idAsociacion: Number(formData.idAsociacion),
        observacion: formData.observacion.trim(),
        idEspecie: Number(formData.idEspecie),
        isEntero: formData.isEntero,
      };

      console.log('DERC Form - Submitting request:', dercRequest);
      await onSubmit(dercRequest);
    } catch (error) {
      console.error('Error submitting DERC form:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, initialDERCRequest, isSubmitting, onSubmit, validateForm]);

  const resetForm = useCallback(() => {
    setFormData(getDefaultFormData());
    setErrors({});
    setIsDirty(false);
  }, [getDefaultFormData]);

  return {
    formData,
    errors,
    isSubmitting,
    isDirty,
    handleFieldChange,
    handleSubmit,
    resetForm,
    validateForm,
  };
};